import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, MoreVertical, Search, Users, Archive, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";

const CLASS_CAPACITY = { sedan: 3, suv: 6, van: 10 };

function getDocStatus(expiryDate) {
  if (!expiryDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(expiryDate + "T00:00:00");
  if (isNaN(expiry.getTime())) return null;
  const diffDays = Math.floor((expiry - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return "expired";
  if (diffDays <= 30) return "expiring";
  return "ok";
}

function DocCircle({ status, testId }) {
  if (status === "expired") return <span className="inline-block w-3 h-3 rounded-full bg-red-500/80" title="Expired" data-testid={testId} />;
  if (status === "expiring") return <span className="inline-block w-3 h-3 rounded-full bg-amber-400/80" title="Expiring soon" data-testid={testId} />;
  if (status === "ok") return <span className="inline-block w-3 h-3 rounded-full bg-emerald-400/80" title="Current" data-testid={testId} />;
  return <span className="text-muted-foreground/30">—</span>;
}

function formatDisplayClass(cls) {
  if (!cls) return "—";
  if (cls.toUpperCase() === "SUV") return "SUV";
  return cls.charAt(0).toUpperCase() + cls.slice(1);
}

const EMPTY_FORM = {
  make: "", model: "", year: "", color: "", plate_number: "", vin: "",
  tlc_vehicle_license: "", license_expiration: "",
  registration_expiry: "", insurance_expiry: "",
  vehicle_type: "sedan", capacity: 3, wav: false,
  base_number: "", base_name: "", base_type: "", base_telephone: "",
  base_website: "", base_address: "",
  registered_owner: "", insurance_policy_number: "", named_insured: "",
  last_inspection_date: "",
  multi_driver: false, notes: "",
};

export default function VehiclesPage() {
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [activeTab, setActiveTab] = useState("info");
  const [lookingUp, setLookingUp] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiveWorking, setArchiveWorking] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-open edit dialog when ?open=<id> is present (e.g., from Licensing page)
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || vehicles.length === 0) return;
    const v = vehicles.find((x) => x.id === openId);
    if (v) {
      openEdit(v);
      searchParams.delete("open");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicles]);

  const loadData = async () => {
    try {
      const [v, d] = await Promise.all([api.get("/vehicles"), api.get("/drivers")]);
      setVehicles(v.data);
      setDrivers(d.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const vehicleDriverMap = useMemo(() => {
    const map = {};
    drivers.forEach((d) => {
      const vid = d.default_vehicle_id || d.vehicle_id;
      if (vid) map[vid] = d;
    });
    return map;
  }, [drivers]);

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const updateClass = (cls) => {
    setForm((prev) => ({ ...prev, vehicle_type: cls }));
  };

  const tlcLookup = async () => {
    const plate = form.plate_number?.trim();
    const tlc = form.tlc_vehicle_license?.trim();
    if (!plate && !tlc) { toast.error("Enter a DMV Plate # or TLC License # first"); return; }
    setLookingUp(true);
    try {
      const params = plate ? `plate=${encodeURIComponent(plate)}` : `tlc=${encodeURIComponent(tlc)}`;
      const { data } = await api.get(`/vehicles/tlc-lookup?${params}`);
      setForm((prev) => ({
        ...prev,
        vin: data.vin || prev.vin,
        year: data.year || prev.year,
        plate_number: data.plate_number || prev.plate_number,
        tlc_vehicle_license: data.tlc_vehicle_license || prev.tlc_vehicle_license,
        license_expiration: data.license_expiration || prev.license_expiration,
        wav: data.wav ?? prev.wav,
        base_number: data.base_number || prev.base_number,
        base_name: data.base_name || prev.base_name,
        base_type: data.base_type || prev.base_type,
        base_telephone: data.base_telephone || prev.base_telephone,
        base_website: data.base_website || prev.base_website,
        base_address: data.base_address || prev.base_address,
        tlc_last_date_updated: data.tlc_last_date_updated || "",
        tlc_last_time_updated: data.tlc_last_time_updated || "",
        tlc_name: data.tlc_name || "",
        tlc_status: data.tlc_status || "active",
      }));
      toast.success("TLC data loaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "TLC lookup failed");
    } finally { setLookingUp(false); }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setActiveTab("info");
    setShowDialog(true);
  };

  const openEdit = (v) => {
    setEditing(v);
    setForm({
      make: v.make || "", model: v.model || "", year: v.year || "", color: v.color || "",
      plate_number: v.plate_number || "", vin: v.vin || "",
      tlc_vehicle_license: v.tlc_vehicle_license || "",
      license_expiration: v.license_expiration || "",
      registration_expiry: v.registration_expiry || "",
      insurance_expiry: v.insurance_expiry || "",
      vehicle_type: v.vehicle_type || "sedan",
      capacity: v.capacity || CLASS_CAPACITY[v.vehicle_type] || 3,
      wav: v.wav || false,
      base_number: v.base_number || "", base_name: v.base_name || "",
      base_type: v.base_type || "", base_telephone: v.base_telephone || "",
      base_website: v.base_website || "", base_address: v.base_address || "",
      registered_owner: v.registered_owner || "",
      insurance_policy_number: v.insurance_policy_number || "",
      named_insured: v.named_insured || "",
      last_inspection_date: v.last_inspection_date || "",
      multi_driver: v.multi_driver || false,
      notes: v.notes || "",
      tlc_last_date_updated: v.tlc_last_date_updated || "",
      tlc_last_time_updated: v.tlc_last_time_updated || "",
      tlc_name: v.tlc_name || "",
    });
    setActiveTab("info");
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.make || !form.model || !form.plate_number) { toast.error("Make, Model, and Plate # are required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, capacity: parseInt(form.capacity) || 3 };
      if (editing) {
        await api.put(`/vehicles/${editing.id}`, payload);
        toast.success("Vehicle updated");
      } else {
        await api.post("/vehicles", payload);
        toast.success("Vehicle added");
      }
      setShowDialog(false);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const handleArchive = async (v) => {
    setArchiveWorking(true);
    try {
      const newArchived = !v.archived;
      await api.put(`/vehicles/${v.id}`, { archived: newArchived });
      toast.success(newArchived ? "Vehicle archived" : "Vehicle restored");
      setArchiveTarget(null);
      setShowDialog(false);
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to archive"); }
    finally { setArchiveWorking(false); }
  };

  const filtered = useMemo(() => {
    let list = vehicles;
    if (!showArchived) list = list.filter((v) => v.status !== "archived");
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter((v) =>
        v.make?.toLowerCase().includes(q) ||
        v.model?.toLowerCase().includes(q) ||
        v.plate_number?.toLowerCase().includes(q) ||
        (v.vehicle_type || "").toLowerCase().includes(q)
      );
    }
    return [...list].sort((a, b) => {
      const driverA = vehicleDriverMap[a.id];
      const driverB = vehicleDriverMap[b.id];
      const hasA = !!driverA;
      const hasB = !!driverB;
      if (hasA && !hasB) return -1;
      if (!hasA && hasB) return 1;
      if (hasA && hasB) {
        const numA = parseInt(driverA.driver_number) || 9999;
        const numB = parseInt(driverB.driver_number) || 9999;
        return numA - numB;
      }
      return 0;
    });
  }, [vehicles, searchQuery, showArchived, vehicleDriverMap]);

  return (
    <div data-testid="vehicles-page" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl tracking-tight font-light" style={{ fontFamily: "'Manrope', sans-serif" }}>Vehicles</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your fleet vehicles</p>
        </div>
        <Button onClick={openCreate} data-testid="add-vehicle-button">
          <Plus className="w-4 h-4 mr-1" /> Add Vehicle
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search vehicles..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 bg-card border-border" data-testid="vehicles-search" />
        </div>
        <div className="flex items-center gap-2" data-testid="vehicles-show-archived-toggle">
          <Switch
            checked={showArchived}
            onCheckedChange={setShowArchived}
            data-testid="vehicles-show-archived-switch"
          />
          <span className="text-xs text-muted-foreground">Show archived</span>
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-4" data-testid="vehicles-doc-legend">
          <div className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-red-500/80" />
            <span className="text-xs text-muted-foreground/80">Expired</span>
          </div>
          <div className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-amber-400/80" />
            <span className="text-xs text-muted-foreground/80">Expires 30 days</span>
          </div>
          <div className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-emerald-400/80" />
            <span className="text-xs text-muted-foreground/80">Current</span>
          </div>
        </div>
      </div>

      <div className="border border-border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/20 hover:bg-muted/20">
              <TableHead>Plate #</TableHead>
              <TableHead>Make</TableHead>
              <TableHead>Model</TableHead>
              <TableHead>Class</TableHead>
              <TableHead className="w-[60px]">PAX #</TableHead>
              <TableHead className="text-center">Driver</TableHead>
              <TableHead className="w-[100px]">Status</TableHead>
              <TableHead className="w-[50px] text-center">Ins.</TableHead>
              <TableHead className="w-[50px] text-center">Reg.</TableHead>
              <TableHead className="w-[70px] text-center">TLC Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={11} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={11} className="text-center py-8 text-muted-foreground">No vehicles found</TableCell></TableRow>
            ) : (
              filtered.map((v) => {
                const insStatus = getDocStatus(v.insurance_expiry);
                const regStatus = getDocStatus(v.registration_expiry);
                const assignedDriver = vehicleDriverMap[v.id];
                return (
                  <TableRow
                    key={v.id}
                    className="cursor-pointer hover:bg-muted/20 transition-colors"
                    onClick={() => openEdit(v)}
                    data-testid={`vehicle-row-${v.id}`}
                  >
                    <TableCell className="font-semibold text-sm" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>{v.plate_number}</TableCell>
                    <TableCell className="text-sm">{v.make}</TableCell>
                    <TableCell className="text-sm">{v.model}</TableCell>
                    <TableCell className="text-sm">{formatDisplayClass(v.vehicle_type)}</TableCell>
                    <TableCell className="text-sm text-center" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>{v.capacity}</TableCell>
                    <TableCell className="text-sm text-center">
                      {assignedDriver ? (
                        <span className="font-semibold text-cyan-300" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>
                          {assignedDriver.driver_number || "—"}
                        </span>
                      ) : (
                        <span className="text-muted-foreground/40">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {v.status === "archived" ? (
                        <Badge className="bg-slate-500/15 text-slate-400 border-slate-500/30 text-[10px]" data-testid={`vehicle-status-${v.id}`}>Archived</Badge>
                      ) : v.status === "assigned" ? (
                        <Badge className="bg-blue-500/15 text-blue-400 border-blue-500/30 text-[10px]" data-testid={`vehicle-status-${v.id}`}>Assigned</Badge>
                      ) : (
                        <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 text-[10px]" data-testid={`vehicle-status-${v.id}`}>Available</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center"><DocCircle status={insStatus} testId={`vehicle-ins-${v.id}`} /></TableCell>
                    <TableCell className="text-center"><DocCircle status={regStatus} testId={`vehicle-reg-${v.id}`} /></TableCell>
                    <TableCell className="text-center">
                      {v.tlc_status === "active" && <span className="inline-block w-3 h-3 rounded-full bg-emerald-400/80" title="TLC Active" data-testid={`vehicle-tlc-status-${v.id}`} />}
                      {v.tlc_status === "suspended" && <span className="inline-block w-3 h-3 rounded-full bg-red-500/80" title="TLC Suspended" data-testid={`vehicle-tlc-status-${v.id}`} />}
                      {!v.tlc_status && <span className="text-muted-foreground/30">—</span>}
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`vehicle-actions-${v.id}`}><MoreVertical className="w-4 h-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-card border-border">
                          <DropdownMenuItem onClick={() => openEdit(v)}>Edit</DropdownMenuItem>
                          {v.status === "archived" ? (
                            <DropdownMenuItem onClick={() => handleArchive(v)} className="text-primary">Restore</DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => setArchiveTarget(v)} className="text-red-400">Archive</DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* ========== VEHICLE DIALOG ========== */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[hsl(152,18%,10%)] border-border max-w-4xl max-h-[90vh] overflow-y-auto p-0" data-testid="vehicle-dialog">
          <DialogHeader className="px-6 pt-5 pb-0 pr-12">
            <div className="flex items-center justify-between">
              <DialogTitle style={{ fontFamily: "'Manrope', sans-serif" }}>
                {editing ? "Edit Vehicle" : "Add Vehicle"}
              </DialogTitle>
              <div className="flex items-center gap-2" data-testid="multi-driver-toggle">
                <Users className="w-4 h-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground/80">Multi-Driver</span>
                <Switch
                  checked={form.multi_driver}
                  onCheckedChange={(v) => updateField("multi_driver", v)}
                  data-testid="vehicle-multi-driver-switch"
                />
              </div>
            </div>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="px-6 pt-3">
            <TabsList className="grid grid-cols-2 w-[240px]" data-testid="vehicle-tabs">
              <TabsTrigger value="info" data-testid="vtab-info">Vehicle Info</TabsTrigger>
              <TabsTrigger value="docs" data-testid="vtab-docs">Documents</TabsTrigger>
            </TabsList>

            {/* ====== TAB 1: VEHICLE INFO ====== */}
            <TabsContent value="info" className="space-y-4 py-4" data-testid="vtab-info-content">
              {/* Row 1 */}
              <div className="grid grid-cols-4 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground/80">VIN</Label>
                  <Input value={form.vin} onChange={(e) => updateField("vin", e.target.value)} className="bg-[hsl(152,15%,7%)] border-[hsl(152,15%,18%)]" data-testid="vehicle-vin-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground/80">Year</Label>
                  <Input value={form.year} onChange={(e) => updateField("year", e.target.value)} className="bg-[hsl(152,15%,7%)] border-[hsl(152,15%,18%)]" data-testid="vehicle-year-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground/80">Color</Label>
                  <Input value={form.color} onChange={(e) => updateField("color", e.target.value)} className="bg-[hsl(152,15%,7%)] border-[hsl(152,15%,18%)]" data-testid="vehicle-color-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground/80">Make *</Label>
                  <Input value={form.make} onChange={(e) => updateField("make", e.target.value)} className="bg-[hsl(152,15%,7%)] border-[hsl(152,15%,18%)]" data-testid="vehicle-make-input" />
                </div>
              </div>
              {/* Row 2 */}
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground/80">Model *</Label>
                  <Input value={form.model} onChange={(e) => updateField("model", e.target.value)} className="bg-[hsl(152,15%,7%)] border-[hsl(152,15%,18%)]" data-testid="vehicle-model-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground/80">DMV License Plate # *</Label>
                  <div className="flex gap-1.5">
                    <Input value={form.plate_number} onChange={(e) => updateField("plate_number", e.target.value)} className="bg-[hsl(152,15%,7%)] border-[hsl(152,15%,18%)] flex-1" data-testid="vehicle-plate-input" />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={tlcLookup}
                      disabled={lookingUp}
                      className="border-primary/40 text-primary hover:bg-primary/10 shrink-0 h-9 px-3 text-xs"
                      data-testid="vehicle-tlc-lookup-btn"
                    >
                      {lookingUp ? "..." : "Lookup"}
                    </Button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground/80">TLC Veh License #</Label>
                  <Input value={form.tlc_vehicle_license} onChange={(e) => updateField("tlc_vehicle_license", e.target.value)} className="bg-[hsl(152,15%,7%)] border-[hsl(152,15%,18%)]" data-testid="vehicle-tlc-license" />
                </div>
              </div>
              {/* Row 3 */}
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground/80">License Expiration</Label>
                  <Input type="date" value={form.license_expiration} onChange={(e) => updateField("license_expiration", e.target.value)} className="bg-[hsl(152,15%,7%)] border-[hsl(152,15%,18%)]" data-testid="vehicle-license-exp" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground/80">Class</Label>
                  <Select value={form.vehicle_type} onValueChange={updateClass}>
                    <SelectTrigger className="bg-[hsl(152,15%,7%)] border-[hsl(152,15%,18%)]" data-testid="vehicle-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sedan">Sedan</SelectItem>
                      <SelectItem value="suv">SUV</SelectItem>
                      <SelectItem value="van">Van</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground/80">PAX #</Label>
                    <Input type="number" min={1} max={20} value={form.capacity} onChange={(e) => updateField("capacity", e.target.value)} className="bg-[hsl(152,15%,7%)] border-[hsl(152,15%,18%)]" data-testid="vehicle-capacity-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground/80">WAV</Label>
                    <div className="flex items-center h-9 px-3 rounded-md bg-muted/20 border border-border">
                      <Switch
                        checked={form.wav}
                        onCheckedChange={(v) => updateField("wav", v)}
                        data-testid="vehicle-wav-switch"
                      />
                      <span className="ml-2 text-xs text-muted-foreground/80">{form.wav ? "Y" : "N"}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Base Info Separator */}
              <div className="border-t border-border pt-4 mt-2">
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-medium mb-3">Base Information</div>
              </div>
              {/* Row 4 */}
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground/80">Base #</Label>
                  <Input value={form.base_number} onChange={(e) => updateField("base_number", e.target.value)} className="bg-[hsl(152,15%,7%)] border-[hsl(152,15%,18%)]" data-testid="vehicle-base-number" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground/80">Base Name</Label>
                  <Input value={form.base_name} onChange={(e) => updateField("base_name", e.target.value)} className="bg-[hsl(152,15%,7%)] border-[hsl(152,15%,18%)]" data-testid="vehicle-base-name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground/80">Base Type</Label>
                  <Select value={form.base_type} onValueChange={(v) => updateField("base_type", v)}>
                    <SelectTrigger className="bg-[hsl(152,15%,7%)] border-[hsl(152,15%,18%)]" data-testid="vehicle-base-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="black_car">Black Car</SelectItem>
                      <SelectItem value="livery">Livery</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {/* Row 5 */}
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground/80">Base Telephone</Label>
                  <Input value={form.base_telephone} onChange={(e) => updateField("base_telephone", e.target.value)} className="bg-[hsl(152,15%,7%)] border-[hsl(152,15%,18%)]" data-testid="vehicle-base-phone" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground/80">Base Website</Label>
                  <Input value={form.base_website} onChange={(e) => updateField("base_website", e.target.value)} className="bg-[hsl(152,15%,7%)] border-[hsl(152,15%,18%)]" data-testid="vehicle-base-website" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground/80">Base Address</Label>
                  <Input value={form.base_address} onChange={(e) => updateField("base_address", e.target.value)} className="bg-[hsl(152,15%,7%)] border-[hsl(152,15%,18%)]" data-testid="vehicle-base-address" />
                </div>
              </div>

              {/* TLC Last Updated */}
              {(form.tlc_last_date_updated || form.tlc_last_time_updated) && (
                <div className="border-t border-border pt-3 mt-2 flex items-center gap-4">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground/60">TLC Last Updated</span>
                  <span className="text-xs text-muted-foreground" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>
                    {form.tlc_last_date_updated || "—"} {form.tlc_last_time_updated ? `at ${form.tlc_last_time_updated}` : ""}
                  </span>
                </div>
              )}

              {/* Notes */}
              <div className="space-y-1.5 pt-2">
                <Label className="text-xs text-muted-foreground/80">Notes</Label>
                <textarea
                  value={form.notes}
                  onChange={(e) => updateField("notes", e.target.value)}
                  rows={2}
                  className="w-full rounded-md bg-[hsl(152,15%,7%)] border border-[hsl(152,15%,18%)] px-3 py-2 text-sm text-foreground resize-none focus:outline-none focus:ring-1 focus:ring-primary"
                  data-testid="vehicle-notes"
                />
              </div>
            </TabsContent>
            <TabsContent value="docs" className="space-y-4 py-4" data-testid="vtab-docs-content">
              <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-medium mb-1">Registration</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground/80">Registered Owner</Label>
                  <Input value={form.registered_owner} onChange={(e) => updateField("registered_owner", e.target.value)} className="bg-[hsl(152,15%,7%)] border-[hsl(152,15%,18%)]" data-testid="vehicle-reg-owner" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground/80">Registration Expiration</Label>
                  <Input type="date" value={form.registration_expiry} onChange={(e) => updateField("registration_expiry", e.target.value)} className="bg-[hsl(152,15%,7%)] border-[hsl(152,15%,18%)]" data-testid="vehicle-reg-expiry" />
                </div>
              </div>

              <div className="border-t border-border pt-4 mt-2">
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-medium mb-3">Insurance</div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground/80">Insurance Policy #</Label>
                  <Input value={form.insurance_policy_number} onChange={(e) => updateField("insurance_policy_number", e.target.value)} className="bg-[hsl(152,15%,7%)] border-[hsl(152,15%,18%)]" data-testid="vehicle-ins-policy" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground/80">Named Insured</Label>
                  <Input value={form.named_insured} onChange={(e) => updateField("named_insured", e.target.value)} className="bg-[hsl(152,15%,7%)] border-[hsl(152,15%,18%)]" data-testid="vehicle-named-insured" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground/80">Insurance Expiration</Label>
                  <Input type="date" value={form.insurance_expiry} onChange={(e) => updateField("insurance_expiry", e.target.value)} className="bg-[hsl(152,15%,7%)] border-[hsl(152,15%,18%)]" data-testid="vehicle-ins-expiry" />
                </div>
              </div>

              <div className="border-t border-border pt-4 mt-2">
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-medium mb-3">Inspection</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground/80">Last Inspection Date</Label>
                  <Input type="date" value={form.last_inspection_date} onChange={(e) => updateField("last_inspection_date", e.target.value)} className="bg-[hsl(152,15%,7%)] border-[hsl(152,15%,18%)]" data-testid="vehicle-last-inspection" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground/80">Next Inspection Due</Label>
                  <div className="flex items-center h-9 px-3 rounded-md bg-[hsl(152,15%,7%)] border border-[hsl(152,15%,18%)] text-sm" style={{ fontFamily: "'IBM Plex Sans', monospace" }} data-testid="vehicle-next-inspection">
                    {form.last_inspection_date ? (() => {
                      const [y, m, d] = form.last_inspection_date.split("-").map(Number);
                      const next = new Date(y, m - 1 + 4, d);
                      const nm = String(next.getMonth() + 1).padStart(2, "0");
                      const nd = String(next.getDate()).padStart(2, "0");
                      const ny = next.getFullYear();
                      return `${nm}/${nd}/${ny}`;
                    })() : <span className="text-muted-foreground/40">Auto-calculated</span>}
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="px-6 py-4 border-t border-border">
            {editing && (
              editing.archived ? (
                <Button
                  variant="outline"
                  onClick={() => handleArchive(editing)}
                  disabled={archiveWorking}
                  className="mr-auto border-primary/40 text-primary hover:bg-primary/10"
                  data-testid="vehicle-restore-dialog-btn"
                >
                  <ArchiveRestore className="w-4 h-4 mr-1.5" /> Restore Vehicle
                </Button>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => setArchiveTarget(editing)}
                  className="mr-auto border-red-500/40 text-red-400 hover:bg-red-500/10"
                  data-testid="vehicle-archive-dialog-btn"
                >
                  <Archive className="w-4 h-4 mr-1.5" /> Archive Vehicle
                </Button>
              )
            )}
            <Button variant="ghost" onClick={() => setShowDialog(false)} data-testid="vehicle-cancel-button">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-vehicle-button">
              {saving ? "Saving..." : editing ? "Update Vehicle" : "Add Vehicle"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== ARCHIVE CONFIRMATION ========== */}
      <AlertDialog open={!!archiveTarget} onOpenChange={(open) => !open && setArchiveTarget(null)}>
        <AlertDialogContent className="bg-card border-border" data-testid="archive-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this vehicle?</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              The vehicle will be hidden from active lists but preserved for historical records.
              Any driver currently assigned to it will have this vehicle removed as their default.
              You can restore the vehicle at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="archive-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => archiveTarget && handleArchive(archiveTarget)}
              disabled={archiveWorking}
              className="bg-red-500/20 text-red-400 border-red-500/40 hover:bg-red-500/30"
              data-testid="archive-confirm-btn"
            >
              {archiveWorking ? "Archiving..." : "Archive"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
